import { useParams, useNavigate } from "react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { DetailPage } from "@/archetypes/DetailPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/hooks/use-confirm";
import { toast } from "@/hooks/use-toast";
import { EmployeeBranchesTab } from "./EmployeeBranchesTab";

interface EmployeeDto {
  id: number; firstName: string; lastName: string; fullName: string;
  email: string | null; phone: string | null; mobile: string | null; fax: string | null;
  jobTitle: string | null; defaultDocketPrinterId: number | null; defaultStickerPrinterId: number | null;
  approvalLimitCents: number | null; signature: string | null; mfaEnabled: boolean; deleted: boolean;
  userId: string | null; roles: string[]; identityLockedOut: boolean;
}
interface UserAuditDto { auditRecId: number; eventType: string; eventDescription: string; createdOn: string; ipAddress: string | null; }

const ALL_ROLES = ["Admin", "SalesLead", "Sales", "Production", "Artwork", "Finance", "ReadOnly"];

export function EmployeeDetailPage() {
  const { id = "0" } = useParams();
  const nav = useNavigate(); const confirm = useConfirm();
  const qc = useQueryClient();
  const empQ = useQuery({ queryKey: ["employee", id], queryFn: () => apiFetch<EmployeeDto>(`/api/employees/${id}`) });
  const auditQ = useQuery({ queryKey: ["employee-audit", id],
    queryFn: () => apiFetch<{ items: UserAuditDto[] }>(`/api/user-audit?employeeId=${id}&pageSize=20`),
    enabled: !!empQ.data?.id });

  const updateMut = useMutation({
    mutationFn: (req: Partial<EmployeeDto>) => apiFetch<EmployeeDto>(`/api/employees/${id}`, { method: "PATCH", body: JSON.stringify(req) }),
    onSuccess: () => { toast.success("Saved"); void qc.invalidateQueries({ queryKey: ["employee", id] }); },
  });
  const setRolesMut = useMutation({
    mutationFn: (roles: string[]) => apiFetch(`/api/employees/${id}/roles`, { method: "POST", body: JSON.stringify({ roles }) }),
    onSuccess: () => { toast.success("Roles updated"); void qc.invalidateQueries({ queryKey: ["employee", id] }); },
  });
  const sendResetMut = useMutation({
    mutationFn: () => apiFetch(`/api/employees/${id}/send-password-reset`, { method: "POST" }),
    onSuccess: () => toast.success("Reset link sent"),
  });
  const unlockMut = useMutation({
    mutationFn: () => apiFetch(`/api/employees/${id}/unlock`, { method: "POST" }),
    onSuccess: () => { toast.success("Unlocked"); void qc.invalidateQueries({ queryKey: ["employee", id] }); },
  });
  const deleteMut = useMutation({
    mutationFn: () => apiFetch(`/api/employees/${id}`, { method: "DELETE" }),
    onSuccess: () => { toast.success("Soft-deleted"); void nav("/admin/employees"); },
  });

  if (!empQ.data) return null;
  const e = empQ.data;

  async function handleDelete() {
    const deps = await apiFetch<{ openQuotes: number; openJobs: number; isBranchManager: boolean }>(`/api/employees/${id}/dependencies`);
    const message = deps.isBranchManager
      ? "This employee manages a branch. Reassign in SA-5 before deleting? Continue anyway?"
      : `Open quotes: ${deps.openQuotes}, open jobs: ${deps.openJobs}. Continue?`;
    if (await confirm({ title: "Soft-delete employee", body: message })) deleteMut.mutate();
  }

  return (
    <DetailPage
      title={`${e.fullName}${e.deleted ? " (deleted)" : ""}`}
      primaryAction={<Button variant="destructive" onClick={() => { void handleDelete(); }}>Soft-delete</Button>}
      tabs={[
        { id: "profile", label: "Profile", content: (
          <ProfileForm employee={e} onSubmit={(req) => updateMut.mutate(req)} pending={updateMut.isPending} />
        )},
        { id: "identity", label: "Identity & Access", content: (
          <div className="space-y-4">
            <div>
              <h3 className="font-medium">Roles</h3>
              <RolesEditor current={e.roles} onSave={(r) => setRolesMut.mutate(r)} />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => sendResetMut.mutate()}>Send password reset</Button>
              {e.identityLockedOut && <Button variant="outline" onClick={() => unlockMut.mutate()}>Unlock account</Button>}
            </div>
            <div><strong>MFA enabled:</strong> {e.mfaEnabled ? "Yes" : "No (admin must enrol via /me)"}</div>
          </div>
        )},
        { id: "defaults", label: "Defaults", content: (
          <DefaultsForm employee={e} onSubmit={(req) => updateMut.mutate(req)} pending={updateMut.isPending} />
        )},
        { id: "branches", label: "Branches", content: (
          <EmployeeBranchesTab employeeId={e.id} hasUserId={e.userId != null} />
        )},
        { id: "activity", label: "Activity", content: (
          <ul className="space-y-2 text-sm">
            {(auditQ.data?.items ?? []).map((a) => (
              <li key={a.auditRecId}><strong>{a.eventType}</strong> {a.eventDescription} <span className="text-muted-foreground">({a.createdOn})</span></li>
            ))}
            {(auditQ.data?.items ?? []).length === 0 && <li className="text-muted-foreground">No recent events.</li>}
          </ul>
        )},
      ]}
    />
  );
}

function ProfileForm({ employee, onSubmit, pending }: { employee: EmployeeDto; onSubmit: (r: Partial<EmployeeDto>) => void; pending: boolean }) {
  const [s, setS] = useState({ firstName: employee.firstName, lastName: employee.lastName,
    email: employee.email ?? "", phone: employee.phone ?? "", mobile: employee.mobile ?? "",
    fax: employee.fax ?? "", jobTitle: employee.jobTitle ?? "", signature: employee.signature ?? "" });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...employee, ...s }); }} className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div><Label htmlFor="fn">First name</Label><Input id="fn" value={s.firstName} onChange={(e) => setS({ ...s, firstName: e.target.value })} required /></div>
      <div><Label htmlFor="ln">Last name</Label><Input id="ln" value={s.lastName} onChange={(e) => setS({ ...s, lastName: e.target.value })} required /></div>
      <div><Label htmlFor="em">Email</Label><Input id="em" type="email" value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} required /></div>
      <div><Label htmlFor="ph">Phone</Label><Input id="ph" value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} /></div>
      <div><Label htmlFor="mo">Mobile</Label><Input id="mo" value={s.mobile} onChange={(e) => setS({ ...s, mobile: e.target.value })} /></div>
      <div><Label htmlFor="ti">Job title</Label><Input id="ti" value={s.jobTitle} onChange={(e) => setS({ ...s, jobTitle: e.target.value })} /></div>
      <div className="md:col-span-2"><Label htmlFor="sig">Signature (HTML)</Label><Input id="sig" value={s.signature} onChange={(e) => setS({ ...s, signature: e.target.value })} /></div>
      <div className="md:col-span-2"><Button type="submit" disabled={pending}>Save profile</Button></div>
    </form>
  );
}

function RolesEditor({ current, onSave }: { current: string[]; onSave: (roles: string[]) => void }) {
  const [sel, setSel] = useState(new Set(current));
  return (
    <div className="space-y-2">
      {ALL_ROLES.map((r) => (
        <label key={r} className="flex items-center gap-2">
          <Switch checked={sel.has(r)} onCheckedChange={(checked) => {
            const next = new Set(sel);
            if (checked) next.add(r); else next.delete(r);
            setSel(next);
          }} />
          <span>{r}</span>
        </label>
      ))}
      <Button onClick={() => onSave([...sel])}>Save roles</Button>
    </div>
  );
}

function DefaultsForm({ employee, onSubmit, pending }: { employee: EmployeeDto; onSubmit: (r: Partial<EmployeeDto>) => void; pending: boolean }) {
  const [s, setS] = useState({
    defaultDocketPrinterId: employee.defaultDocketPrinterId ?? null,
    defaultStickerPrinterId: employee.defaultStickerPrinterId ?? null,
    approvalLimitCents: employee.approvalLimitCents ?? null,
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...employee, ...s }); }} className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div><Label htmlFor="dp">Default docket printer ID</Label><Input id="dp" type="number" value={s.defaultDocketPrinterId ?? ""} onChange={(e) => setS({ ...s, defaultDocketPrinterId: e.target.value ? Number(e.target.value) : null })} /></div>
      <div><Label htmlFor="sp">Default sticker printer ID</Label><Input id="sp" type="number" value={s.defaultStickerPrinterId ?? ""} onChange={(e) => setS({ ...s, defaultStickerPrinterId: e.target.value ? Number(e.target.value) : null })} /></div>
      <div><Label htmlFor="al">Approval limit (cents)</Label><Input id="al" type="number" value={s.approvalLimitCents ?? ""} onChange={(e) => setS({ ...s, approvalLimitCents: e.target.value ? Number(e.target.value) : null })} /></div>
      <div className="md:col-span-2"><Button type="submit" disabled={pending}>Save defaults</Button></div>
    </form>
  );
}
